/**
 * Shared storage for Zustand stores  →  the BACKEND database.
 * ---------------------------------------------------------------------------
 * Every store saves its data as one JSON blob to the backend KV store
 * (/api/store/:key, backed by PostgreSQL). That means the data is:
 *   - the SAME for every device/browser that opens the app,
 *   - kept in the database, so it survives refresh AND server restart,
 *   - only ever removed when someone explicitly deletes it.
 *
 * localStorage is kept ONLY as a same-device cache/offline fallback. Crucially,
 * if a read comes back empty or errors, we fall back to that cache instead of
 * returning "nothing" — otherwise a single bad read could make a store think it
 * is empty and re-seed the defaults over real data (the old disappearing-data
 * bug). We only ever return null (i.e. "truly first run, please seed") when the
 * server is reachable, empty, AND we have no local cache either.
 */
import type { StateStorage } from 'zustand/middleware'

// Frontend calls /api/store; Vite proxies /api → backend (localhost:5000).
const BASE = '/api/store'

export const sharedStorage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    try {
      const res = await fetch(`${BASE}/${encodeURIComponent(name)}`, { cache: 'no-store' })
      if (res.ok) {
        const txt = await res.text()
        if (txt && txt.length) {
          localStorage.setItem(name, txt) // refresh same-device cache
          return txt
        }
        // Server reachable but has NO row for this key. Only treat as "first
        // run, seed defaults" if we ALSO have no local cache. If we do have a
        // cache (real data from before), keep it — never wipe.
        return localStorage.getItem(name)
      }
      // Server responded with an error → use the last good cached copy.
      return localStorage.getItem(name)
    } catch {
      // Backend unreachable/offline → use the last good cached copy.
      return localStorage.getItem(name)
    }
  },

  setItem: async (name: string, value: string): Promise<void> => {
    localStorage.setItem(name, value) // keep a same-device cache too
    try {
      await fetch(`${BASE}/${encodeURIComponent(name)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: value,
      })
    } catch {
      /* offline — the value is safe in localStorage and will re-sync on the next change */
    }
  },

  removeItem: async (name: string): Promise<void> => {
    localStorage.removeItem(name)
    try { await fetch(`${BASE}/${encodeURIComponent(name)}`, { method: 'DELETE' }) } catch { /* ignore */ }
  },
}
